import * as yup from 'yup';
import DeleteAssetModal from 'components/CleanRooms/DeleteAssetModal';
import InfoIcon from '@mui/icons-material/Info';
import LoadingButton from '@mui/lab/LoadingButton';
import PermissionService from 'components/Common/PermissionService';
import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Divider,
  FormHelperText,
  Link,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { ExpandMore } from '@mui/icons-material';
import { FLOW_QUERY_KEYS } from 'utils/constants/flow.constant';
import { FlowBuilderContext } from 'pages/Flow/FlowBuilder/FlowBuilderContextProvider';
import { QUESTION_QUERY_KEYS } from 'utils/constants/question.constant';
import { habuColors } from 'Theme';
import { organizationManagement } from 'utils/spaUrls';
import { productPerm } from 'utils/appConstants';
import { useCreateExportJob } from 'hooks/activationAPI';
import { useFetchOrgPartnerAccounts } from 'hooks/activationChannelsAPI';
import { useFetchQuestionActivationOptions } from 'api/questions';
import { useGetFlowNodeExportJobs } from 'hooks/flowExportAPI';
import { useGetIdentityList } from 'api/OrganizationIdentity';
import { usePrepareTableForAssets } from 'api/questions/usePrepareTableForAssets';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';


const FlowNodeSegmentationConfiguration = ({
  cleanRoomQuestion,
  node,
  onSubmitRef,
  shouldSubmitRef,
  isSubmittingRef,
}) => {
  const { crId, flowId } = useContext(FlowBuilderContext);
  const queryClient = useQueryClient();
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);
  const isTranscodingEnabled = PermissionService.isProductEnabled([productPerm.Transcoding]);
  const isCRActivationV1Enabled = PermissionService.isProductEnabled(
    [productPerm.cleanRoomActivationV1]);

  // Form validation schema
  const validationSchema = yup.object().shape({
    assetDisplayName: yup.string()
      .required()
      .min(3)
      .max(128)
      .matches(/^(?!\d+$)[a-zA-Z0-9_-]+$/, 'Table Name cannot be pure digital, contain spaces, or symbols besides _ and -')
      .label('Table Name'),
    rampIdColumn: yup.string().required().label('Ramp ID Column'),
    domainIdentifierID: yup.string()
      .when([], {
        is: () => isTranscodingEnabled && isCRActivationV1Enabled,
        then: (schema) => schema.required().matches(/^[A-Za-z0-9]{4}$/, 'Domain Identifier must be 4 alphanumeric characters').label('Domain Identifier'),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const {
    control, handleSubmit, setValue, watch, formState: { errors },
  } = useForm({
    defaultValues: {
      assetDisplayName: cleanRoomQuestion?.assetDisplayName?.length ? cleanRoomQuestion?.assetDisplayName : cleanRoomQuestion?.displayID ?? '',
      rampIdColumn: '',
      domainIdentifierID: '',
    },
    resolver: yupResolver(validationSchema),
  });

  // Fetch the following by the hook:
  // identityField: field of the clean room question
  // identityType: not used in this component
  // partnerAccount: destinations of this organization. Not used in this component.
  const {
    isSuccess,
    isFetching,
    isError,
    data: questionActivationOptions,
  } = useFetchQuestionActivationOptions(
    crId, cleanRoomQuestion?.ID,
  );

  const {
    isLoading: isFetchingIdentities,
    isSuccess: isFetchingIdentitiesSuccess,
    isError: isFetchingIdentitiesError,
    data: { domainIdentifiers = [] } = {},
  } = useGetIdentityList();

  const {
    data: partnerAccount,
  } = useFetchOrgPartnerAccounts(orgId, {
    filters: [
      { attribute: 'Partner', values: ['LR-Activation'] },
    ],
    sortBy: {},
    limit: 1,
    offset: 0,
    search: '',
  });

  const {
    mutateAsync: prepareTableForAssets,
  } = usePrepareTableForAssets();

  const {
    mutateAsync: createExportJob,
  } = useCreateExportJob();

  // Fetch existing export jobs for this node
  // enabled: false means it won't fetch automatically on mount
  // We'll manually refetch when form is submitted
  const {
    refetch: refetchExportJobs,
  } = useGetFlowNodeExportJobs({
    crId,
    flowId,
    nodeId: node?.id,
    enabled: false, // Only fetch when manually triggered
  });

  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTablePrepared, setIsTablePrepared] = useState(cleanRoomQuestion?.isAsset || false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Track initial values to detect changes
  const initialValuesRef = React.useRef({
    assetDisplayName: cleanRoomQuestion?.assetDisplayName?.length ? cleanRoomQuestion?.assetDisplayName : cleanRoomQuestion?.displayID ?? '',
    rampIdColumn: cleanRoomQuestion?.AssetRampIDColumnName || '',
    domainIdentifierID: '',
  });

  // Track if form has been submitted to prevent resets after user actions
  const hasBeenSubmittedRef = useRef(false);

  // Create a ref to handle data refresh callback
  const dataContextRef = useRef();

  // Update the callback ref whenever dependencies change
  useEffect(() => {
    dataContextRef.current = {
      refreshListData: () => {
        // After delete, clear the configuration state
        setIsTablePrepared(false);
        // Reset the submitted flag to allow form to reinitialize
        hasBeenSubmittedRef.current = false;
        // Reset form to unconfigured default values
        const defaultAssetDisplayName = cleanRoomQuestion?.displayID ?? '';
        const defaultRampIdColumn = questionActivationOptions?.identityField?.[0]?.name ?? '';
        setValue('assetDisplayName', defaultAssetDisplayName);
        setValue('rampIdColumn', defaultRampIdColumn);
        // Update initial values ref to match the reset values
        initialValuesRef.current = {
          assetDisplayName: defaultAssetDisplayName,
          rampIdColumn: defaultRampIdColumn,
          domainIdentifierID: domainIdentifiers[0]?.domainIdentifierID || '',
        };
        setHasChanges(false);
      },
    };
  }, [cleanRoomQuestion, questionActivationOptions, domainIdentifiers, setValue]);

  // Initialize/update form values when cleanRoomQuestion data changes
  useEffect(() => {
    if (!cleanRoomQuestion) return;

    // Skip reset if user has already submitted the form
    if (hasBeenSubmittedRef.current) return;

    // Determine if configured based on cleanRoomQuestion.isAsset
    const isConfigured = cleanRoomQuestion.isAsset || false;

    // Set assetDisplayName based on whether it's configured
    const displayNameValue = isConfigured && cleanRoomQuestion?.assetDisplayName?.length
      ? cleanRoomQuestion?.assetDisplayName
      : cleanRoomQuestion?.displayID ?? '';
    setValue('assetDisplayName', displayNameValue);
    initialValuesRef.current.assetDisplayName = displayNameValue;

    // Update isTablePrepared to match actual data
    setIsTablePrepared(isConfigured);
  }, [cleanRoomQuestion, setValue]);

  useEffect(() => {
    if (questionActivationOptions && cleanRoomQuestion) {
      // Skip reset if user has already submitted the form
      if (hasBeenSubmittedRef.current) return;

      const isConfigured = cleanRoomQuestion.isAsset || false;
      // Set rampIdColumn based on whether it's configured
      const rampIdValue = isConfigured
        && cleanRoomQuestion?.AssetRampIDColumnName?.length
        ? cleanRoomQuestion?.AssetRampIDColumnName
        : questionActivationOptions?.identityField?.[0]?.name ?? '';
      setValue('rampIdColumn', rampIdValue);
      initialValuesRef.current.rampIdColumn = rampIdValue;
    }
  }, [questionActivationOptions, cleanRoomQuestion, setValue]);

  useEffect(() => {
    if (
      isFetchingIdentitiesSuccess
      && domainIdentifiers.length > 0
      && isTranscodingEnabled
      && isCRActivationV1Enabled
    ) {
      const domainId = domainIdentifiers[0]?.domainIdentifierID || '';
      setValue('domainIdentifierID', domainId);
      // Update initial values ref
      initialValuesRef.current.domainIdentifierID = domainId;
    }
  }, [
    isFetchingIdentitiesSuccess,
    domainIdentifiers,
    isTranscodingEnabled,
    isCRActivationV1Enabled,
    setValue,
  ]);

  // Watch form values to detect changes
  const formValues = watch();

  useEffect(() => {
    const currentValues = {
      assetDisplayName: formValues.assetDisplayName || '',
      rampIdColumn: formValues.rampIdColumn || '',
      domainIdentifierID: formValues.domainIdentifierID || '',
    };

    const hasChanged = currentValues.assetDisplayName !== initialValuesRef.current.assetDisplayName
      || currentValues.rampIdColumn !== initialValuesRef.current.rampIdColumn
      || currentValues.domainIdentifierID !== initialValuesRef.current.domainIdentifierID;

    setHasChanges(hasChanged);
  }, [formValues]);

  // Handle form submission
  // Create or update the table in Data Catalog
  const onSubmit = useCallback(async (values) => {
    // Only proceed if:
    // 1. Not yet configured (isTablePrepared is false), OR
    // 2. Already configured AND has changes
    if (isTablePrepared && !hasChanges) {
      return;
    }

    setIsSubmitting(true);
    // Update ref synchronously so parent sees the change immediately
    if (isSubmittingRef) {
      // eslint-disable-next-line no-param-reassign
      isSubmittingRef.current = true;
    }

    try {
      await prepareTableForAssets({
        cleanRoomId: crId,
        questionId: cleanRoomQuestion?.ID, // use clean room question ID instead of question ID
        isUpdate: isTablePrepared, // use local state instead of cleanRoomQuestion?.isAsset
        payload: {
          assetDisplayName: values.assetDisplayName ?? '',
          rampID: values.rampIdColumn ?? '',
          ...(isTranscodingEnabled && isCRActivationV1Enabled && {
            domainIdentifierID: values.domainIdentifierID ?? '',
          }),
        },
      });
      // Mark that form has been submitted to prevent auto-resets
      hasBeenSubmittedRef.current = true;
      // After successful preparation, update the state to indicate table is now prepared
      setIsTablePrepared(true);
      // Update initial values to current values after successful save
      initialValuesRef.current = {
        assetDisplayName: values.assetDisplayName ?? '',
        rampIdColumn: values.rampIdColumn ?? '',
        domainIdentifierID: values.domainIdentifierID ?? '',
      };
      setHasChanges(false);

      // Early return if partnerAccountID is not available
      const partnerAccountID = partnerAccount?.data?.partnerAccounts[0]?.ID;
      if (!partnerAccountID) {
        return;
      }

      // create export job if question is not an asset
      // Fetch existing export jobs to check if LR-Activation job already exists
      const { data: fetchedExportJobs } = await refetchExportJobs();

      // Check if any existing export job has partnerAccountPartnerName === 'LR-Activation'
      const hasLRActivationJob = fetchedExportJobs?.jobs?.some(
        job => job.partnerAccountPartnerName === 'LR-Activation' && job.status === 'ACTIVE',
      );

      if (hasLRActivationJob) {
        return;
      }

      // Only create export job if:
      // 1. LR-Activation destination exists
      // 2. No existing LR-Activation export job exists
      await createExportJob({
        cleanRoomID: crId,
        cleanRoomQuestionID: cleanRoomQuestion?.ID,
        partnerAccountIDs: [partnerAccountID],
        exportParameters: [],
      });

      // Invalidate export jobs cache to trigger re-render of FlowNodeExportConfiguration
      queryClient.invalidateQueries([FLOW_QUERY_KEYS.FLOW_NODE_EXPORT_JOBS,
        crId, flowId, node?.id]);
    }
    catch (error) {
      // Error handling is managed by the hook
    }
    finally {
      setIsSubmitting(false);
      // Update ref synchronously so parent sees the change immediately
      if (isSubmittingRef) {
        // eslint-disable-next-line no-param-reassign
        isSubmittingRef.current = false;
      }

      // Invalidate clean room question query to trigger re-render of parent component
      // This ensures the parent re-renders and re-checks the isSubmitting ref
      queryClient.invalidateQueries([QUESTION_QUERY_KEYS.GET_CLEANROOM_QUESTION_V2, orgId, crId]);
    }
  }, [
    hasChanges,
    isTablePrepared,
    prepareTableForAssets,
    crId,
    flowId,
    node?.id,
    cleanRoomQuestion?.ID,
    isTranscodingEnabled,
    isCRActivationV1Enabled,
    createExportJob,
    partnerAccount?.data?.partnerAccounts,
    refetchExportJobs,
    queryClient,
    orgId,
    isSubmittingRef,
  ]);

  // Expose submit function and shouldSubmit flag via refs to parent
  React.useEffect(() => {
    // Allow submission if:
    // 1. Not yet configured (isTablePrepared is false), OR
    // 2. Already configured AND has changes
    const shouldAllowSubmit = !isTablePrepared || hasChanges;

    if (onSubmitRef) {
      // eslint-disable-next-line no-param-reassign
      onSubmitRef.current = handleSubmit(onSubmit);
    }
    if (shouldSubmitRef) {
      // eslint-disable-next-line no-param-reassign
      shouldSubmitRef.current = shouldAllowSubmit;
    }
  }, [
    onSubmitRef,
    shouldSubmitRef,
    hasChanges,
    isTablePrepared,
    handleSubmit,
    onSubmit,
  ]);

  // Handle delete - show modal
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  // Handle modal close
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <Accordion defaultExpanded disableGutters elevation={0} style={{ width: '100%' }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{ background: (theme) => theme.palette.secondary.light }}
        >
          <Typography variant='subtitle1'>
            Segmentation Configuration
            <Typography component='span' variant='caption' sx={{ ml: 1, color: isTablePrepared ? 'success.main' : habuColors.primary[100], fontWeight: 500 }}>
              {isTablePrepared ? '(Configured)' : '(Unconfigured)'}
            </Typography>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <form onSubmit={(e) => e.preventDefault()}>
            <Stack spacing={3}>
              {isFetching || isFetchingIdentities ? (
                <Stack spacing={2}>
                  <Skeleton variant='rectangular' height={40} />
                  <Skeleton variant='rectangular' height={40} />
                  {isTranscodingEnabled && isCRActivationV1Enabled && <Skeleton variant='rectangular' height={40} />}
                </Stack>
              ) : (
                <>
                  {isSuccess ? (
                    <Stack spacing={2}>
                      {/* Table Name Field */}
                      <Controller
                        name='assetDisplayName'
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id='input-table-name'
                            label='Table Name *'
                            fullWidth
                            size='small'
                            placeholder='Type table name'
                            variant='outlined'
                            error={!!errors?.assetDisplayName}
                            helperText={errors?.assetDisplayName?.message}
                          />
                        )}
                      />

                      {/* Ramp ID Column Field */}
                      <Controller
                        name='rampIdColumn'
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            size='small'
                            id='select-ramp-id-column'
                            data-testid='select-ramp-id-column'
                            options={
                              questionActivationOptions?.identityField?.map(
                                option => option.name,
                              ) || []
                            }
                            value={field.value || ''}
                            getOptionLabel={option => option}
                            disableClearable
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label='Select Ramp ID Column *'
                                size='small'
                                variant='outlined'
                                error={!!errors?.rampIdColumn}
                              />
                            )}
                            onChange={(event, value) => field.onChange(value)}
                          />
                        )}
                      />
                      <FormHelperText error>{errors?.rampIdColumn?.message}</FormHelperText>

                      {/* Domain Identifier Field */}
                      {isTranscodingEnabled && isCRActivationV1Enabled && (
                        <>
                          <Stack direction='row' alignItems='center' justifyContent='center' spacing={1}>
                            <Controller
                              name='domainIdentifierID'
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  id='input-domain-identifier'
                                  label='RampID Domain Identifier *'
                                  fullWidth
                                  size='small'
                                  placeholder='Type RampID Domain Identifier'
                                  variant='outlined'
                                  disabled
                                  error={!!errors?.domainIdentifierID}
                                />
                              )}
                            />
                            <Tooltip
                              title={(
                                <Typography>
                                  Go to
                                  {' '}
                                  <Link href={organizationManagement.organization.identity.list} target='_self' rel='noopener noreferrer'>
                                    Identity
                                  </Link>
                                  {' '}
                                  to change the RampID Domain Identifier for your organization.
                                </Typography>
                              )}
                              placement='right'
                            >
                              <span style={{ marginTop: '1rem' }}>
                                <InfoIcon aria-label='More info about RampID Domain Identifier' />
                              </span>
                            </Tooltip>
                          </Stack>
                          <FormHelperText>4 alphanumeric characters</FormHelperText>
                          <FormHelperText error>
                            {errors?.domainIdentifierID?.message}
                          </FormHelperText>
                        </>
                      )}
                    </Stack>
                  ) : (
                    <>
                      {isError && (
                        <Alert severity='error'>
                          <Typography variant='body2'>
                            Error fetching options, please try again later.
                          </Typography>
                        </Alert>
                      )}
                      {isFetchingIdentitiesError && (
                        <Alert severity='error'>
                          <Typography variant='body2'>
                            Error fetching identities, please try again later.
                          </Typography>
                        </Alert>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Action Buttons */}
              <Stack direction='row' spacing={2} justifyContent='flex-end'>
                <LoadingButton
                  variant='outlined'
                  color='error'
                  data-testid='delete-button'
                  onClick={handleDeleteClick}
                  disabled={!isTablePrepared || isSubmitting}
                >
                  Delete Configuration
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        </AccordionDetails>
        <Divider />
      </Accordion>

      {/* Delete Asset Modal */}
      {showDeleteModal && (
        <DeleteAssetModal
          crId={crId}
          question={cleanRoomQuestion}
          dataContextRef={dataContextRef}
          onClose={handleCloseDeleteModal}
        />
      )}
    </>
  );
};

export default FlowNodeSegmentationConfiguration;
